
import COrientationMixin from './COrientationMixin.vue'
import { defineComponent } from '~/utils/nuxt3-migration'

export default defineComponent({
  mixins: [COrientationMixin],
  props: {
    acceptedFiles: {
      type: Array,
      default: () => ['application/pdf', 'image/jpeg', 'image/png', 'image/gif']
    },
    maxFileSize: { type: Number, default: 50 }, // in MB
    parallelUploads: { type: Number, default: 1 } // 1 will wait for a file to finish and then upload the next,
  },
  data() {
    return {
      files: [],
      addIndx: 0,
      queueList: [],
      uploadingCount: 0,
      lastParallelIndex: 0,
      uploading: false
    }
  },
  computed: {
    uploadUrl() {
      // every uploader must have this computed property set separately, by default it has the attachment url
      return '/api/account/messages/attachments/'
    },
    uploadMethod() {
      return 'post'
    },
    acceptedFilesString() {
      // to be used in the template input element like :accept="acceptedFilesString"
      let str = ''
      for (let i = 0; i < this.acceptedFiles.length; i++) {
        str += this.acceptedFiles[i]
        if (i + 1 < this.acceptedFiles.length) {
          str += ','
        }
      }

      return str
    },
    acceptedFileExtensions() {
      let str = ''
      for (let i = 0; i < this.acceptedFiles.length; i++) {
        str += this.acceptedFiles[i]
          .replace('image/', '')
          .replace('application/', '')
        if (i + 1 < this.acceptedFiles.length) {
          str += ', '
        }
      }

      return str
    },
    haserrors() {
      // checks if any file has error
      for (let i = 0; i < this.files.length; i++) {
        if (this.files[i].error || this.files[i]['failed-validation']) {
          return true
        }
      }
      return false
    },
    canPostQueue() {
      return this.queueList.length
    }
  },
  methods: {
    emitForm() {
      // emit is different from component to component
    },
    addFiles(f) {
      this.filesInput(f)
    },
    filesInput(e) {
      let currentFiles
      if (e.dataTransfer) {
        currentFiles = e.dataTransfer.files
      } else if (e.target) {
        currentFiles = e.target.files
      }
      if (currentFiles === null || !currentFiles.length) {
        return
      }

      if (!this.checkFilesOnInput(currentFiles)) {
        return
      }

      if (!this.checkIfFilesAccepted(currentFiles)) {
        alert(this.$t('this file type is not supported'))
        return
      }
      for (let i = 0; i < currentFiles.length; i++) {
        this.files.push(this.createFileFromInput(currentFiles[i]))
      }

      const origFile = this.files[this.addIndx]
      this.readFile(origFile, currentFiles.length)
    },
    createFileFromInput(currentFile) {
      return {
        file: currentFile,
        originalFilename: currentFile.name,
        filename: currentFile.name,
        uploading: false,
        percentage: 0,
        thumb: null,
        error: null,
        approved: false,
        type: null,
        deleted: false,
        warning: ''
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    checkFilesOnInput(currentFiles) {
      // use this method in component if you need extra checks
      return true
    },
    realAddFile(origFile, currentFilesLength) {
      this.files[this.addIndx] = origFile
      // load next or add to queue
      this.addIndx++
      if (this.addIndx < this.files.length) {
        this.readFile(this.files[this.addIndx], currentFilesLength)
      } else {
        // this means: "start uploading once all the local thumbs are loaded"
        for (
          let i = this.addIndx - currentFilesLength;
          i < this.files.length;
          i++
        ) {
          this.queueFile(i)
        }
        // once we got all the files queued and no file is uploading, start posting
        if (!this.uploading) {
          this.postQueue()
        }
      }
    },
    fileNotAnImage(_origFile, _currentFilesLength) {},
    queueFile(indx) {
      this.queueList.push(indx)
    },
    postQueue() {
      // calculate how many empty spaces there are for parallel upload
      if (this.canPostQueue) {
        const emptySpaces =
          Math.min(this.queueList.length, this.parallelUploads) -
          this.uploadingCount

        for (
          let i = this.lastParallelIndex;
          i < this.lastParallelIndex + emptySpaces;
          i++
        ) {
          if (this.files[i].file.size / 1024000 < this.maxFileSize) {
            this.postFile(this.files[i])
          } else {
            let errorMsg = this.$t('file size cannot exceed')
            errorMsg = errorMsg + ' ' + this.maxFileSize + 'MB'
            this.files[i].error = errorMsg
            this.editFileAfterSizeEror(i)

            if (this.lastParallelIndex + emptySpaces >= this.addIndx) {
              this.uploading = false
              // this.lastParallelIndex = this.addIndx
              this.queueList = []
              this.afterQueueComplete()
            } else if (this.uploadingCount < this.parallelUploads) {
              this.lastParallelIndex++ // this is some crazy logic over here
            }
          }
        }

        this.lastParallelIndex += emptySpaces // this is basically the next index that can be uploaded
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    editFileAfterSizeError(index) {
      // populate this if you want to edit something in file after size error
    },
    afterAllRequestsComplete() {
      // if you want to run stuff after all requests are done
    },
    afterQueueComplete() {
      // populate this method for delete cleaners in component
    },
    postFile(theFile) {
      this.uploadingCount++ // increase every time we start an upload

      theFile.uploading = true
      this.uploading = true

      const formData = new FormData()
      formData.append('file', theFile.file)

      this.$nextTick(function() {
        this.$axios({
          method: this.uploadMethod,
          url: this.urlForFile(theFile),
          data: formData,
          onUploadProgress(progressEvent) {
            const percentage = parseInt(
              Math.round((progressEvent.loaded * 100) / progressEvent.total)
            )
            theFile.percentage = percentage
          }
        })
          .then(response => {
            theFile.uploading = false

            if (response.status === 200) {
              this.onPostSuccess(response, theFile)
            }

            this.handleQueueOnResponse()
          })
          .catch(error => {
            theFile.uploading = false
            this.onPostError(error, theFile)
            this.handleQueueOnResponse()
          })
      })
    },
    onPostSuccess(response, theFile) {
      if (response.data.data.file) {
        theFile.approved = true
        theFile.filename = response.data.data.file.name
        this.emitForm()
      }
    },
    onPostError(error, theFile) {
      if (error.response.data.error) {
        theFile.error = error.response.data.error
      } else {
        theFile.error = this.$t(
          'there was an error with uploading this file, please try again later'
        )
      }
    },
    handleQueueOnResponse() {
      this.uploadingCount--

      if (this.lastParallelIndex >= this.addIndx) {
        this.uploading = false
        this.lastParallelIndex = this.addIndx
        this.queueList = []

        this.afterQueueComplete()
        this.afterAllRequestsComplete()
      } else if (this.uploadingCount < this.parallelUploads) {
        this.postQueue()
      }
    },
    urlForFile(file) {
      // overwrite this method from the component if you need different url relative to file type etc.
      if (file) {
        return this.uploadUrl
      }
    },
    checkIfFilesAccepted(files) {
      for (let i = 0; i < files.length; i++) {
        let accept = false

        for (let j = 0; j < this.acceptedFiles.length; j++) {
          if (files[i].type.match(this.acceptedFiles[j])) {
            accept = true
            break
          }
        }

        if (!accept) {
          return false
        }
      }

      return true
    }
  }
})
